<template>
  <!-- Players Section -->
  <div class="w-full mx-auto py-4">
    <table class="w-full mb-4">
      <thead>
        <tr class="text-left">
          <th class="pb-2">Name</th>
          <th class="pb-2">No</th>
          <th class="pb-2">Size</th>
          <th class="pb-2">Qty</th>
          <th class="pb-2">C/A</th>
          <th class="pb-2"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(player, index) in players"
          :key="`${'manual_roster_player_' + index}`"
          class="border-t"
        >
          <td class="py-2">
            <div>
              <input
                v-model="player.text"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500': errorMessages[`players[${index}].text`],
                }"
                :title="errorMessages[`players[${index}].text`]"
                @focusout="syncPlayersWithParent"
              />
            </div>
          </td>
          <td class="py-2">
            <input
              v-model="player.number"
              type="text"
              @input="(e: Event) => handlePlayerNumberInput(e, index)"
              @focusout="syncPlayersWithParent"
              class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </td>
          <td class="py-2">
            <div v-if="isCustomProduct">
              <input
                v-model="player.size"
                @focusout="syncPlayersWithParent"
                type="text"
                class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500 focus:ring-0': errorMessages[`players[${index}].size`],
                }"
                :title="errorMessages[`players[${index}].size`]"
              />
            </div>
            <div v-else>
              <div class="flex w-100">
                <ExclamationCircleIcon
                  v-if="errorMessages[`players[${index}].size`]"
                  class="text-red-500"
                  style="font-size: 2px; height: 25px; width: 25px"
                />
                <Select
                  v-model="player.size"
                  :options="product_sizes"
                  style="width: 100%"
                  :clearable="false"
                  append-to-body
                  placement="auto"
                  @focusout="syncPlayersWithParent"
                  :title="errorMessages[`players[${index}].size`]"
                  @option:selected="(val) => handlePlayerSizeSelect(index, val)"
                >
                  <template #option="{ label }">
                    <p class="whitespace-normal max-w-[200px]">{{ label }}</p>
                  </template>
                </Select>
              </div>
            </div>
          </td>
          <td class="py-2">
            <div>
              <input
                v-model="player.quantity"
                min="1"
                type="number"
                @focusout="syncPlayersWithParent"
                @keydown.tab="handleTabKey(index, $event)"
                class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                :class="{
                  'border-red-500 focus:ring-0': errorMessages[`players[${index}].quantity`],
                }"
                :title="errorMessages[`players[${index}].quantity`]"
              />
            </div>
          </td>
          <td class="py-2">
            <select
              v-model="player.captains_associates"
              @change="syncPlayersWithParent"
              class="w-20 px-3 py-2 border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-</option>
              <option value="C">C</option>
              <option value="A">A</option>
            </select>
          </td>
          <td class="py-2">
            <button
              type="button"
              :key="`remove_player_${index}`"
              @click="() => removePlayer(index)"
              class="p-2 text-red-500 hover:bg-red-50 rounded-full"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <button
      type="button"
      @click="addPlayer"
      class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
    >
      Add Player
    </button>
    <button
      type="button"
      @click="copyRoster"
      class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors ml-2"
    >
      Copy Roster
    </button>
    <button v-if="copiedRoster.isAnyRosterCopied && copiedRoster.copiedFromProductIndex !== index"
      type="button"
      @click="pasteRoster"
      class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors ml-2"
    >
      Paste Roster from product {{ Number(copiedRoster.copiedFromProductIndex) + 1 }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { defineEmits, defineProps, onMounted, ref, watch } from 'vue';
import Select from './Select.vue';
import { CopiedRoster } from '@/models/CopiedRoster';

// Props being used in the template
const props = defineProps<{
  players: Array<any>;
  product_sizes: string[] | Array<any>;
  isCustomProduct: boolean | false;
  index: number | 0;
  copiedRoster?: CopiedRoster;
}>();

const emit = defineEmits(['update:players','copy:roster']);

let errorMessages = ref([]);

onMounted(() => {
  if (Array.isArray(props.players) && props.players.length === 0) {
    props.players.push({
      text: '',
      number: '',
      size: null,
      quantity: 1,
      information: '',
      captains_associates: '',
    });
  }
});

watch(
  () => props.players,
  (newPlayers) => {
    if (Array.isArray(newPlayers) && newPlayers.length === 0) {
      const defaultPlayer = {
        text: '',
        number: '',
        size: null,
        quantity: 1,
        information: '',
        captains_associates: '',
      };
      newPlayers.push(defaultPlayer);
      emit('update:players', props.players, props.index);
    }
  },
  { immediate: true, deep: true }
);

function addPlayer() {

  let prevPlayer
  if (props.players.length > 0) {
    prevPlayer = props.players[props.players.length - 1];
  }

  const newPlayer = {
    text: '',
    number: '',
    size: prevPlayer ? prevPlayer.size : '',
    quantity: 1,
    information: '',
    captains_associates: '',
  };
  // Add the new player to the players array
  props.players.push(newPlayer);
  emit('update:players', props.players, props.index);
}

function removePlayer(index: number) {
  // Remove the player at the specified index
  props.players.splice(index, 1);

  // Emit the updated players list and the current index
  emit('update:players', props.players, props.index);
}

function syncPlayersWithParent() {
  // Emit the updated players list and the current index
  emit('update:players', props.players, props.index);
}
function handleTabKey(index: number, event: KeyboardEvent) {
  if (event.key === 'Tab' && index === props.players.length - 1) {
    addPlayer();
  }
}
function handlePlayerSizeSelect(index: number, selectedSize: string) {
  props.players[index].size = selectedSize;
  syncPlayersWithParent();
}
function handlePlayerNumberInput(event: Event, index: number) {
  const input = event.target as HTMLInputElement;
  // Allow only digits (0-9) and preserve leading zeros
  props.players[index].number = input.value.replace(/[^0-9]/g, '');
  syncPlayersWithParent();
}


function copyRoster() {
  emit('copy:roster',props.players,props.index);
}

function pasteRoster() {
  if(props.copiedRoster && props.copiedRoster.isAnyRosterCopied) {
    let rosterData =  props.copiedRoster.rosterData.map(player => {
      let matchedSize = '';
      if(props.product_sizes && !props.isCustomProduct) {
        props.product_sizes.forEach(size => {
          if(size === player.size) {
            matchedSize = size;
          }
        });
      }
      else if(props.isCustomProduct) {
        matchedSize = player.size;
      }
      return {
        ...player,
        size: matchedSize
      };
    });
    props.players.splice(0, props.players.length, ...rosterData);
    syncPlayersWithParent();
  }
}
</script>
